import { useEffect, useState, useRef, useLayoutEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Users, Building2, Briefcase, CheckSquare, TrendingUp, Trophy } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";

function KPI({ icon: Icon, label, value, sub, testid }) {
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
                <div className="w-9 h-9 rounded-md bg-orange-50 text-orange-600 flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                </div>
            </div>
            <div className="text-3xl font-display font-bold text-slate-900">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}

// Measures its parent and renders the chart with explicit pixel width/height.
// Avoids Recharts' ResponsiveContainer measurement glitches on mobile.
function SizedChart({ height = 256, children }) {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        if (!ref.current) return;
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) {
                const w = Math.floor(e.contentRect.width);
                if (w > 0) setWidth(w);
            }
        });
        ro.observe(ref.current);
        setWidth(ref.current.clientWidth);
        return () => ro.disconnect();
    }, []);
    return (
        <div ref={ref} style={{ width: "100%", height }}>
            {width > 0 && children({ width, height })}
        </div>
    );
}

export default function Dashboard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dashboard/metrics").then(({ data }) => setData(data));
    }, []);

    if (!data) {
        return (
            <Layout title="Panel de Control" subtitle="Resumen de tu actividad comercial">
                <div className="text-slate-500">Cargando...</div>
            </Layout>
        );
    }

    const t = data.totals;

    return (
        <Layout title="Panel de Control" subtitle="Resumen de tu actividad comercial" data-testid="dashboard-page">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <KPI icon={Users} label="Prospectos" value={t.leads} testid="dashboard-kpi-leads" />
                <KPI icon={Building2} label="Empresas" value={t.companies} testid="dashboard-kpi-companies" />
                <KPI icon={Briefcase} label="Oportunidades" value={t.open_deals} sub={`${t.deals} totales`} testid="dashboard-kpi-deals" />
                <KPI icon={CheckSquare} label="Tareas pendientes" value={t.pending_tasks} sub={`${t.overdue_tasks} atrasadas`} testid="dashboard-kpi-tasks" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <KPI icon={TrendingUp} label="Pipeline (USD)" value={`$${(t.pipeline_value || 0).toLocaleString()}`} />
                <KPI icon={Trophy} label="Ganadas (USD)" value={`$${(t.won_value || 0).toLocaleString()}`} />
                <KPI icon={TrendingUp} label="Tasa de conversión" value={`${t.conversion_rate}%`} />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-200 rounded-lg p-5 min-w-0">
                    <h3 className="font-display font-semibold text-slate-900 mb-4">Distribución por etapa</h3>
                    <SizedChart height={256}>
                        {({ width, height }) => (
                            <BarChart width={width} height={height} data={data.stages_distribution}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#64748B" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="count" fill="#F97316" radius={[4,4,0,0]} />
                            </BarChart>
                        )}
                    </SizedChart>
                </div>
                <div className="bg-white border border-slate-200 rounded-lg p-5 min-w-0">
                    <h3 className="font-display font-semibold text-slate-900 mb-4">Negocios por mes</h3>
                    <SizedChart height={256}>
                        {({ width, height }) => (
                            <LineChart width={width} height={height} data={data.deals_by_month}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                                <Tooltip />
                                <Line type="monotone" dataKey="count" stroke="#F97316" strokeWidth={2} dot={{ r: 4 }} />
                            </LineChart>
                        )}
                    </SizedChart>
                </div>
            </div>
        </Layout>
    );
}
